import {
  deleteChatForEveryone,
  deleteChatForMe,
  deleteMessageForEveryone,
  deleteMessageForMe,
  sendChatMessage,
  sendMediaMessage,
  sendVoiceMessage,
  setCurrentChatContactId,
  syncInboundMessages,
  armEphemeralCountdown,
  onEphemeralCountdownExpired,
  ephemeralRemainingSeconds,
  isEphemeralMessage,
} from '../../app/zionApp.ts'
import {
  getMessageTimerTtl,
  MESSAGE_TIMER_OPTIONS,
  setMessageTimerTtl,
} from '../../chat/messageTimer.ts'
import { clearUnreadForChat } from '../../storage/unreadStore.ts'
import { loadMessages, onMessagesChanged, deleteMessage, type ChatMessage } from '../../storage/chatStore.ts'
import { isTempChatControlPlain } from '../../tempChat/tempChatControl.ts'
import { isZionControlPlain } from '../../chat/zionControl.ts'
import { contactDisplayName, findContact, renameContact, type ZionContact } from '../../storage/pairingStore.ts'
import { isChatPinned, toggleChatPinned } from '../../storage/pinnedChatsStore.ts'
import {
  attachComposerInputIosKeyboardFix,
  attachChatVisualViewportInsets,
  keepComposerInputFocused,
  preventButtonFocusSteal,
} from '../composerKeyboard.ts'
import {
  clearComposer,
  getComposerText,
  setComposerText,
  wireComposerEditable,
} from '../composerField.ts'
import { applyAppLayout } from '../layout.ts'
import { unlockNotificationAudio } from '../../notifications/audioUnlock.ts'
import { isOnlyEmoji } from '../emojiOnlyText.ts'
import { getCurrentTheme } from '../themes.ts'
import { appendBlocks, copyText, el, escapeHtml } from '../dom.ts'
import { openExternalUrl, renderMessageHtml } from '../../chat/linkPolicy.ts'
import {
  buildMediaBubbleHtml,
  mediaCaptionHtml,
  revokeMediaObjectUrls,
  wireMediaBubbleActions,
} from '../../chat/chatMediaBubble.ts'
import { isMediaKind, isDocumentFile, formatFileSize, MAX_DOCUMENT_BYTES } from '../../media/zionMediaProtocol.ts'
import { buildSendPreviewMarkup, buildSendTextPreview } from '../../media/documentPreview.ts'
import { CODE_TOO_SHORT, CODE_TOO_LONG } from '../../media/voiceRecorder.ts'
import { wireVoiceMic } from '../voiceMessageUi.ts'
import { t, tf } from '../../i18n/index.ts'
import { formatMessageDayLabel, sameCalendarDay } from '../../chat/messageTime.ts'
import { getParam, navigate } from '../router.ts'
import { wireSwipeBack } from '../swipeBack.ts'
import { scrollIosPwaMessagesIfKeyboardOpen } from '../pwaKeyboardFix.ts'

export { setupPWAKeyboardFix, scrollIosPwaMessagesIfKeyboardOpen } from '../pwaKeyboardFix.ts'

let chatPollTimer: number | null = null
let chatAudioUnlockHandler: (() => void) | null = null
let chatScreenRoot: HTMLElement | null = null
let chatVisibilityHandler: (() => void) | null = null
let chatMessageUnsub: (() => void) | null = null
let voiceMicCleanup: (() => void) | null = null
let chatSwipeCleanup: (() => void) | null = null
let chatKeyboardCleanup: (() => void) | null = null
let composerFieldCleanup: (() => void) | null = null
let ephemeralTickTimer: number | null = null
const ephemeralArmed = new Set<string>()
const ephemeralExpiring = new Set<string>()

/** Bozze composer per contatto — sopravvivono a repaint/sync mentre si digita. */
const composerDrafts = new Map<string, string>()

interface ReplyDraft {
  id: string
  preview: string
}

export function clearChatScreenHooks(): void {
  if (chatPollTimer != null) {
    window.clearInterval(chatPollTimer)
    chatPollTimer = null
  }
  if (chatVisibilityHandler) {
    document.removeEventListener('visibilitychange', chatVisibilityHandler)
    chatVisibilityHandler = null
  }
  if (chatMessageUnsub) {
    chatMessageUnsub()
    chatMessageUnsub = null
  }
  if (voiceMicCleanup) {
    voiceMicCleanup()
    voiceMicCleanup = null
  }
  if (chatSwipeCleanup) {
    chatSwipeCleanup()
    chatSwipeCleanup = null
  }
  if (chatKeyboardCleanup) {
    chatKeyboardCleanup()
    chatKeyboardCleanup = null
  }
  if (composerFieldCleanup) {
    composerFieldCleanup()
    composerFieldCleanup = null
  }
  if (chatAudioUnlockHandler && chatScreenRoot) {
    chatScreenRoot.removeEventListener('pointerdown', chatAudioUnlockHandler)
    chatScreenRoot.removeEventListener('touchstart', chatAudioUnlockHandler)
    chatAudioUnlockHandler = null
    chatScreenRoot = null
  }
  if (ephemeralTickTimer != null) {
    window.clearInterval(ephemeralTickTimer)
    ephemeralTickTimer = null
  }
  ephemeralArmed.clear()
  ephemeralExpiring.clear()
  document.querySelector('#rename-modal')?.remove()
  document.querySelector('#msg-action-sheet')?.remove()
  document.querySelector('#chat-menu-sheet')?.remove()
  document.querySelector('#doc-preview-modal')?.remove()
  document.querySelector('#doc-viewer-modal')?.remove()
  document.querySelector('#chat-jump-bottom')?.remove()
  revokeMediaObjectUrls()
}

export async function renderChatScreen(root: HTMLElement): Promise<void> {
  clearChatScreenHooks()

  const contactId = getParam('contactId')
  const contact = await findContact(contactId)
  if (!contact) {
    navigate('home')
    return
  }
  setCurrentChatContactId(contactId)
  clearUnreadForChat(contactId, contact.sessionToken)

  chatScreenRoot = root
  chatAudioUnlockHandler = () => {
    void unlockNotificationAudio()
  }
  root.addEventListener('pointerdown', chatAudioUnlockHandler, { passive: true })
  root.addEventListener('touchstart', chatAudioUnlockHandler, { passive: true })

  let liveContact = contact
  let replyDraft: ReplyDraft | null = null
  const renameLabel = getCurrentTheme() === 'matrix' ? t('chat_rename_matrix') : t('chat_rename')
  const menuLabel = getCurrentTheme() === 'matrix' ? '[···]' : '···'

  applyAppLayout('chat')
  root.classList.remove('screen-dashboard')
  root.classList.add('screen-chat', 'chat-screen')
  root.innerHTML = ''
  appendBlocks(
    root,
    `
      <header class="topbar premium-topbar chat-topbar">
        <button type="button" class="icon-btn" id="btn-back">←</button>
        <div class="topbar-contact">
          <h2 class="topbar-title" id="contact-title">${isChatPinned(contactId) ? '📌 ' : ''}${escapeHtml(contactDisplayName(liveContact))}</h2>
          <small class="topbar-sub" id="contact-sub">@${escapeHtml(liveContact.nickname)}</small>
        </div>
        <button type="button" class="icon-btn rename-btn" id="btn-rename" title="${escapeHtml(t('chat_rename_title'))}">${escapeHtml(renameLabel)}</button>
        <button type="button" class="icon-btn" id="btn-chat-menu" title="${escapeHtml(t('chat_menu_title'))}">${escapeHtml(menuLabel)}</button>
      </header>
      <div class="chat-messages" id="messages">
        <p class="status muted chat-empty">${escapeHtml(t('chat_loading'))}</p>
      </div>
      <div class="chat-footer chat-input-bar" id="chat-input-bar">
      <div id="reply-bar" class="reply-bar hidden" hidden>
        <div class="reply-bar-text">
          <span class="reply-bar-label">${escapeHtml(t('chat_reply_to'))}</span>
          <span class="reply-bar-preview" id="reply-preview"></span>
        </div>
        <button type="button" class="icon-btn reply-bar-cancel" id="btn-cancel-reply" title="${escapeHtml(t('chat_cancel_title'))}">✕</button>
      </div>
      <div class="voice-recording-banner" id="voice-rec-banner" hidden>
        <div class="voice-rec-meta">
          <span class="voice-rec-label" id="voice-rec-label">${escapeHtml(t('chat_voice_lock_hint'))}</span>
          <span class="voice-rec-time" id="voice-rec-time">0:00</span>
        </div>
        <div class="voice-rec-actions">
          <button type="button" class="icon-btn" id="voice-rec-cancel" title="${escapeHtml(t('chat_cancel_title'))}">✕</button>
          <button type="button" class="icon-btn voice-rec-stop" id="voice-rec-stop" title="${escapeHtml(t('chat_voice_stop_title'))}" hidden>${escapeHtml(t('chat_voice_stop'))}</button>
        </div>
      </div>
      <div class="voice-preview-banner" id="voice-preview-banner" hidden>
        <div class="voice-preview-meta">
          <span class="voice-preview-label">${escapeHtml(t('chat_voice_preview_label'))}</span>
          <audio id="voice-preview-audio" preload="metadata"></audio>
          <button type="button" class="voice-preview-play" id="voice-preview-play">▶</button>
          <span class="voice-preview-time" id="voice-preview-time">0:00</span>
        </div>
        <div class="voice-preview-actions">
          <button type="button" class="icon-btn" id="voice-preview-cancel" title="${escapeHtml(t('chat_cancel_title'))}">✕</button>
          <button type="button" class="icon-btn voice-preview-send" id="voice-preview-send" title="${escapeHtml(t('chat_voice_send_title'))}">${escapeHtml(t('chat_voice_send'))}</button>
        </div>
      </div>
      <div class="composer-attach-panel" id="attach-panel" hidden>
        <div class="timer-picker-row" id="timer-picker" hidden>
          ${MESSAGE_TIMER_OPTIONS.map((opt) => `<button type="button" class="timer-opt" data-ttl="${opt.ttlMs ?? ''}">${opt.label}</button>`).join('')}
        </div>
        <div class="attach-menu-row" id="attach-menu">
          <button type="button" class="attach-menu-btn" id="btn-timer" title="${escapeHtml(t('chat_timer_title'))}">
            <span class="attach-menu-icon">⏱</span><span class="attach-menu-label">${escapeHtml(t('chat_timer_label'))}</span>
          </button>
          <button type="button" class="attach-menu-btn" id="btn-photo" title="${escapeHtml(t('chat_photo_title'))}">
            <span class="attach-menu-icon">◎</span><span class="attach-menu-label">${escapeHtml(t('chat_photo'))}</span>
          </button>
          <button type="button" class="attach-menu-btn voice-mic-btn" id="btn-voice" title="${escapeHtml(t('chat_voice_hold_title'))}">
            <span class="attach-menu-icon">🎤</span><span class="attach-menu-label">${escapeHtml(t('chat_voice_hold'))}</span>
          </button>
          <button type="button" class="attach-menu-btn" id="btn-document" title="${escapeHtml(t('chat_doc_title'))}">
            <span class="attach-menu-icon">📎</span><span class="attach-menu-label">${escapeHtml(t('chat_doc'))}</span>
          </button>
        </div>
      </div>
      <form class="chat-composer" id="composer">
        <input
          type="file"
          id="media-input"
          class="ios-safe-file-input"
          accept="image/*,.gif,.webp,.png,.jpg,.jpeg,.heic,.heif"
          multiple
        />
        <input
          type="file"
          id="document-input"
          class="ios-safe-file-input"
          accept=".pdf,.docx,.doc,.txt,.zip,.rar,.xlsx,.pptx,.gif,image/gif,application/pdf,application/zip,application/x-zip-compressed,text/plain"
        />
        <button type="button" class="icon-btn attach-toggle-btn" id="btn-attach-toggle" title="${escapeHtml(t('chat_attach_title'))}">+</button>
        <div
          id="msg-input"
          class="msg-input is-empty"
          contenteditable="true"
          role="textbox"
          aria-multiline="false"
          data-placeholder="${escapeHtml(t('chat_input_placeholder'))}"
          inputmode="text"
          enterkeyhint="send"
        ></div>
        <button type="submit" class="send-btn" aria-label="${escapeHtml(t('chat_send'))}">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <line x1="22" y1="2" x2="11" y2="13"></line>
            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
          </svg>
        </button>
      </form>
      </div>
    `,
  )

  const messagesEl = root.querySelector('#messages') as HTMLElement
  const composer = root.querySelector('#composer') as HTMLFormElement
  const msgInput = composer.querySelector('#msg-input') as HTMLElement
  const sendBtn = composer.querySelector('button[type="submit"]') as HTMLButtonElement
  preventButtonFocusSteal(sendBtn)
  attachComposerInputIosKeyboardFix(msgInput)
  msgInput.addEventListener('focus', () => {
    void import('../../notifications/audioUnlock.ts').then((m) => m.unlockNotificationAudio())
  })
  const footerBar = root.querySelector('#chat-input-bar') as HTMLElement
  chatKeyboardCleanup = attachChatVisualViewportInsets({
    app: root,
    footer: footerBar,
    messages: messagesEl,
    input: msgInput,
  })
  const mediaInput = root.querySelector('#media-input') as HTMLInputElement
  const documentInput = root.querySelector('#document-input') as HTMLInputElement
  const replyBar = root.querySelector('#reply-bar') as HTMLElement
  const replyPreviewEl = root.querySelector('#reply-preview') as HTMLElement

  function userIsTypingInComposer(): boolean {
    return getComposerText(msgInput).length > 0
  }

  function snapshotComposerDraft(): void {
    const v = getComposerText(msgInput)
    if (v) composerDrafts.set(contactId, v)
    else composerDrafts.delete(contactId)
  }

  function restoreComposerDraft(): void {
    const draft = composerDrafts.get(contactId)
    if (draft == null) return
    if (getComposerText(msgInput) !== draft) setComposerText(msgInput, draft)
  }

  async function sendPickedMediaFiles(files: File[]): Promise<void> {
    if (!files.length) return
    const statusEl = document.createElement('p')
    statusEl.className = 'status muted small media-send-status'
    statusEl.textContent = files.length > 1 ? `Invio ${files.length} file…` : 'Invio media…'
    footerBar.prepend(statusEl)
    try {
      const caption = getComposerText(msgInput).trim()
      for (const file of files) {
        statusEl.textContent = `Invio ${file.name || 'media'}…`
        await sendMediaMessage(contactId, file, caption)
      }
      clearComposer(msgInput)
      snapshotComposerDraft()
      await paint({ forceScroll: true })
    } catch (err) {
      console.warn('[ZION] send media', err)
      statusEl.textContent = 'Media in coda — riprovo…'
      window.setTimeout(() => statusEl.remove(), 1800)
      return
    } finally {
      if (statusEl.isConnected) statusEl.remove()
    }
  }

  const doSendText = async (): Promise<void> => {
    const text = getComposerText(msgInput).trim()
    if (!text) return
    clearComposer(msgInput)
    composerDrafts.delete(contactId)
    const reply = replyDraft
      ? { replyToId: replyDraft.id, replyPreview: replyDraft.preview }
      : undefined
    clearReplyDraft()
    try {
      await sendChatMessage(contactId, text, reply)
      userReadingHistory = false
      await paint({ forceScroll: true })
      keepComposerInputFocused(msgInput)
    } catch (err) {
      // Solo errori strutturali (es. contatto senza chiave) — ripristina testo, niente alert
      console.warn('[ZION] send text', err)
      setComposerText(msgInput, text)
      await paint({ forceScroll: true })
      keepComposerInputFocused(msgInput)
    }
  }

  composerFieldCleanup = wireComposerEditable(msgInput, {
    onSubmit: () => {
      void doSendText()
    },
    onInput: snapshotComposerDraft,
    onImageFiles: (files) => {
      void sendPickedMediaFiles(files)
    },
  })

  root.querySelector('#btn-back')!.addEventListener('click', () => goBackFromChat())

  const renameModal = el(`
    <div class="modal-overlay" id="rename-modal" hidden>
      <form class="card modal-card" id="rename-form">
        <h3 id="rename-title">${escapeHtml(t('chat_rename_modal_title'))}</h3>
        <p class="status muted small" id="rename-body" hidden></p>
        <p class="status muted small" id="rename-original">${escapeHtml(tf('chat_rename_nickname', { nick: liveContact.nickname }))}</p>
        <input id="rename-input" type="text" maxlength="64" placeholder="${escapeHtml(t('chat_rename_placeholder'))}" autocomplete="off" />
        <div class="row-btns">
          <button type="button" class="secondary" id="rename-cancel">${escapeHtml(t('chat_rename_cancel'))}</button>
          <button type="submit">${escapeHtml(t('chat_rename_save'))}</button>
        </div>
      </form>
    </div>
  `)
  document.body.append(renameModal)

  const msgActionSheet = el(`
    <div class="action-sheet-overlay" id="msg-action-sheet" hidden>
      <div class="action-sheet card">
        <button type="button" class="action-sheet-item" data-action="reply">${escapeHtml(t('chat_action_reply'))}</button>
        <button type="button" class="action-sheet-item" data-action="copy">${escapeHtml(t('chat_action_copy'))}</button>
        <button type="button" class="action-sheet-item" data-action="delete-me">${escapeHtml(t('chat_action_delete_me'))}</button>
        <button type="button" class="action-sheet-item danger" data-action="delete-all">${escapeHtml(t('chat_action_delete_all'))}</button>
        <button type="button" class="action-sheet-item secondary" data-action="cancel">${escapeHtml(t('chat_action_cancel'))}</button>
      </div>
    </div>
  `)
  document.body.append(msgActionSheet)

  const chatMenuSheet = el(`
    <div class="action-sheet-overlay" id="chat-menu-sheet" hidden>
      <div class="action-sheet card">
        <button type="button" class="action-sheet-item" data-action="pin" id="chat-menu-pin">${escapeHtml(t('chat_pin'))}</button>
        <button type="button" class="action-sheet-item" data-action="clear-me">${escapeHtml(t('chat_menu_clear_me'))}</button>
        <button type="button" class="action-sheet-item danger" data-action="clear-all">${escapeHtml(t('chat_menu_clear_all'))}</button>
        <button type="button" class="action-sheet-item secondary" data-action="cancel">${escapeHtml(t('chat_menu_cancel'))}</button>
      </div>
    </div>
  `)
  document.body.append(chatMenuSheet)

  const refreshPinMenuLabel = (): void => {
    const pinBtn = chatMenuSheet.querySelector('#chat-menu-pin') as HTMLButtonElement | null
    if (pinBtn) pinBtn.textContent = isChatPinned(contactId) ? t('chat_unpin') : t('chat_pin')
  }
  refreshPinMenuLabel()

  const docPreviewModal = el(`
    <div class="modal-overlay doc-preview-overlay" id="doc-preview-modal" hidden>
      <div class="card modal-card doc-preview-card">
        <h3>${escapeHtml(t('chat_doc_send_title'))}</h3>
        <div class="doc-preview-box">
          <span class="doc-preview-icon" aria-hidden="true">📎</span>
          <div class="doc-preview-meta">
            <p class="doc-preview-name" id="doc-preview-name">—</p>
            <p class="doc-preview-size" id="doc-preview-size">—</p>
            <p class="doc-preview-status" id="doc-preview-status" hidden></p>
          </div>
        </div>
        <div class="doc-preview-frame" id="doc-preview-frame"></div>
        <p class="doc-preview-error err" id="doc-preview-error" hidden></p>
        <p class="status muted small">${escapeHtml(t('chat_doc_send_hint'))}</p>
        <div class="row-btns">
          <button type="button" class="secondary" id="doc-preview-cancel">${escapeHtml(t('chat_doc_cancel'))}</button>
          <button type="button" id="doc-preview-send">${escapeHtml(t('chat_doc_send_encrypted'))}</button>
        </div>
      </div>
    </div>
  `)
  document.body.append(docPreviewModal)

  let pendingDocumentFile: File | null = null
  let selectedMessage: ChatMessage | null = null
  const sendPreviewUrls = new Map<string, string>()

  function clearSendPreview(): void {
    for (const url of sendPreviewUrls.values()) URL.revokeObjectURL(url)
    sendPreviewUrls.clear()
    const frame = docPreviewModal.querySelector('#doc-preview-frame') as HTMLElement
    if (frame) frame.innerHTML = ''
  }

  function closeDocPreview(): void {
    pendingDocumentFile = null
    clearSendPreview()
    docPreviewModal.hidden = true
    const errEl = docPreviewModal.querySelector('#doc-preview-error') as HTMLElement
    const statusEl = docPreviewModal.querySelector('#doc-preview-status') as HTMLElement
    errEl.hidden = true
    errEl.textContent = ''
    statusEl.hidden = true
    statusEl.textContent = ''
    document.body.style.overflow = ''
  }

  function openDocPreview(file: File): void {
    pendingDocumentFile = file
    clearSendPreview()
    const nameEl = docPreviewModal.querySelector('#doc-preview-name') as HTMLElement
    const sizeEl = docPreviewModal.querySelector('#doc-preview-size') as HTMLElement
    const errEl = docPreviewModal.querySelector('#doc-preview-error') as HTMLElement
    const sendBtn = docPreviewModal.querySelector('#doc-preview-send') as HTMLButtonElement
    const frame = docPreviewModal.querySelector('#doc-preview-frame') as HTMLElement
    nameEl.textContent = file.name || 'documento'
    sizeEl.textContent = formatFileSize(file.size)
    const tooLarge = file.size > MAX_DOCUMENT_BYTES
    errEl.hidden = !tooLarge
    errEl.textContent = tooLarge ? t('chat_doc_too_large') : ''
    sendBtn.disabled = tooLarge
    frame.innerHTML = buildSendPreviewMarkup(file, sendPreviewUrls, 'send')
    void (async () => {
      if (frame.querySelector('.doc-preview-loading')) {
        frame.innerHTML = await buildSendTextPreview(file)
      }
    })()
    docPreviewModal.hidden = false
    document.body.style.overflow = 'hidden'
  }

  function refreshTopbar(c: ZionContact): void {
    const titleEl = root.querySelector('#contact-title') as HTMLElement
    const name = contactDisplayName(c)
    titleEl.textContent = isChatPinned(contactId) ? `📌 ${name}` : name
    root.querySelector('#contact-sub')!.textContent = `@${c.nickname}`
    renameModal.querySelector('#rename-original')!.textContent = tf('chat_rename_nickname', { nick: c.nickname })
  }

  function setReplyDraft(msg: ChatMessage): void {
    replyDraft = { id: msg.id, preview: msg.text.slice(0, 120) }
    replyPreviewEl.textContent = replyDraft.preview
    replyBar.hidden = false
    replyBar.classList.remove('hidden')
    msgInput.focus()
  }

  function clearReplyDraft(): void {
    replyDraft = null
    replyBar.hidden = true
    replyBar.classList.add('hidden')
    replyPreviewEl.textContent = ''
  }

  root.querySelector('#btn-cancel-reply')!.addEventListener('click', clearReplyDraft)

  root.querySelector('#btn-rename')!.addEventListener('click', () => {
    const renameInput = renameModal.querySelector('#rename-input') as HTMLInputElement
    const titleEl = renameModal.querySelector('#rename-title') as HTMLElement
    const bodyEl = renameModal.querySelector('#rename-body') as HTMLElement
    const cancelBtn = renameModal.querySelector('#rename-cancel') as HTMLButtonElement
    titleEl.textContent = t('chat_rename_modal_title')
    bodyEl.hidden = true
    bodyEl.textContent = ''
    cancelBtn.textContent = t('chat_rename_cancel')
    renameInput.value = liveContact.customName?.trim() ?? ''
    renameModal.hidden = false
    renameInput.focus()
  })

  renameModal.querySelector('#rename-cancel')!.addEventListener('click', () => {
    renameModal.hidden = true
  })

  renameModal.addEventListener('click', (e) => {
    if (e.target === renameModal) renameModal.hidden = true
  })

  renameModal.querySelector('#rename-form')!.addEventListener('submit', async (e) => {
    e.preventDefault()
    const renameInput = renameModal.querySelector('#rename-input') as HTMLInputElement
    const updated = await renameContact(contactId, renameInput.value)
    if (!updated) return
    liveContact = updated
    refreshTopbar(updated)
    renameModal.hidden = true
  })

  // Auto-popup dopo accoppiamento gestito da scheduleNewContactRenamePrompt (ui/modals.ts).
  // Qui resta solo il rename manuale dal menu.

  function openMsgActions(msg: ChatMessage): void {
    selectedMessage = msg
    const deleteAllBtn = msgActionSheet.querySelector('[data-action="delete-all"]') as HTMLButtonElement
    deleteAllBtn.style.display = msg.outgoing ? '' : 'none'
    msgActionSheet.hidden = false
  }

  function closeMsgActions(): void {
    msgActionSheet.hidden = true
    selectedMessage = null
  }

  msgActionSheet.addEventListener('click', (e) => {
    if (e.target === msgActionSheet) closeMsgActions()
  })

  msgActionSheet.querySelectorAll('[data-action]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const action = (btn as HTMLElement).dataset.action
      const msg = selectedMessage
      closeMsgActions()
      if (!msg || action === 'cancel') return

      void (async () => {
        if (action === 'reply') {
          setReplyDraft(msg)
          return
        }
        if (action === 'copy') {
          const ok = await copyText(msg.text)
          if (!ok) alert(t('chat_copy_failed'))
          return
        }
        if (action === 'delete-me') {
          if (!confirm(t('chat_confirm_delete_me'))) return
          await deleteMessageForMe(msg.id)
          await paint({ forceScroll: false })
          return
        }
        if (action === 'delete-all') {
          if (!confirm(t('chat_confirm_delete_all'))) return
          const result = await deleteMessageForEveryone(msg.id)
          if (!result.ok) alert(result.message)
          await paint({ forceScroll: false })
        }
      })()
    })
  })

  root.querySelector('#btn-chat-menu')!.addEventListener('click', () => {
    refreshPinMenuLabel()
    chatMenuSheet.hidden = false
  })

  chatMenuSheet.addEventListener('click', (e) => {
    if (e.target === chatMenuSheet) chatMenuSheet.hidden = true
  })

  chatMenuSheet.querySelectorAll('[data-action]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const action = (btn as HTMLElement).dataset.action
      chatMenuSheet.hidden = true
      if (action === 'cancel') return
      void (async () => {
        if (action === 'pin') {
          toggleChatPinned(contactId)
          refreshPinMenuLabel()
          refreshTopbar(liveContact)
          return
        }
        if (action === 'clear-all') {
          if (!confirm(t('chat_confirm_clear_all'))) return
          const result = await deleteChatForEveryone(contactId)
          clearChatScreenHooks()
          setCurrentChatContactId('')
          navigate('home')
          alert(result.message)
          return
        }
        if (action === 'clear-me') {
          if (!confirm(t('chat_confirm_clear_me'))) return
          await deleteChatForMe(contactId)
          clearChatScreenHooks()
          setCurrentChatContactId('')
          navigate('home')
          return
        }
      })()
    })
  })

  let lastPaintedMsgs: ChatMessage[] = []
  let userReadingHistory = false
  let newMessagesBelow = false
  let programmaticScroll = false
  let paintRaf: number | null = null
  let pendingPaintOpts: { forceScroll?: boolean; forceRepaint?: boolean } = {}

  const jumpBtn = el(
    `<button type="button" class="chat-jump-bottom hidden" id="chat-jump-bottom" hidden>${escapeHtml(t('chat_jump_new'))}</button>`,
  )
  messagesEl.after(jumpBtn)

  function isNearBottom(threshold = 120): boolean {
    const box = messagesEl
    return box.scrollHeight - box.scrollTop - box.clientHeight < threshold
  }

  function scrollToLatest(smooth = false): void {
    programmaticScroll = true
    if (smooth) {
      messagesEl.scrollTo({ top: messagesEl.scrollHeight, behavior: 'smooth' })
    } else {
      messagesEl.scrollTop = messagesEl.scrollHeight
    }
    requestAnimationFrame(() => {
      programmaticScroll = false
    })
  }

  function captureScrollAnchor(): { id: string; offset: number } | null {
    const box = messagesEl
    const scrollTop = box.scrollTop
    const bubbles = box.querySelectorAll<HTMLElement>('.bubble[data-msg-id]')
    for (const bubble of bubbles) {
      const top = bubble.offsetTop
      const bottom = top + bubble.offsetHeight
      if (bottom > scrollTop + 8) {
        const id = bubble.dataset.msgId
        if (!id) continue
        return { id, offset: top - scrollTop }
      }
    }
    return null
  }

  function restoreScrollAnchor(anchor: { id: string; offset: number }): void {
    const bubble = messagesEl.querySelector<HTMLElement>(`.bubble[data-msg-id="${anchor.id}"]`)
    if (!bubble) return
    programmaticScroll = true
    messagesEl.scrollTop = Math.max(0, bubble.offsetTop - anchor.offset)
    requestAnimationFrame(() => {
      programmaticScroll = false
    })
  }

  function updateJumpButton(): void {
    jumpBtn.hidden = !newMessagesBelow || !userReadingHistory
    jumpBtn.classList.toggle('hidden', jumpBtn.hidden)
  }

  messagesEl.addEventListener(
    'scroll',
    () => {
      if (programmaticScroll) return
      if (isNearBottom(100)) {
        userReadingHistory = false
        newMessagesBelow = false
        updateJumpButton()
      } else {
        userReadingHistory = true
      }
    },
    { passive: true },
  )

  messagesEl.addEventListener(
    'load',
    (e) => {
      const t = e.target
      if (!(t instanceof HTMLImageElement)) return
      if (!t.classList.contains('media-img') && !t.classList.contains('media-doc-preview-iframe')) return
      if (!userReadingHistory) scrollToLatest()
    },
    true,
  )

  jumpBtn.addEventListener('click', () => {
    userReadingHistory = false
    newMessagesBelow = false
    updateJumpButton()
    scrollToLatest()
  })

  function messageVisualKey(m: ChatMessage): string {
    return `${m.id}:${m.pending ? 1 : 0}:${m.processingStatus ?? ''}:${m.text.length}:${m.mediaKind ?? ''}:${m.countdownStartedAt ?? 0}:${m.readAndDestroyed ? 1 : 0}`
  }

  function messagesChanged(prev: ChatMessage[], next: ChatMessage[]): boolean {
    if (prev.length !== next.length) return true
    for (let i = 0; i < next.length; i++) {
      if (messageVisualKey(prev[i]!) !== messageVisualKey(next[i]!)) return true
    }
    return false
  }

  function messagesEndEl(): HTMLElement {
    let end = messagesEl.querySelector('#messages-end') as HTMLElement | null
    if (!end) {
      end = el('<div id="messages-end" aria-hidden="true"></div>')
      messagesEl.append(end)
    }
    return end
  }

  /** Spacer flex in cima: tiene i messaggi in basso senza margin-top:auto (che rompe lo scroll). */
  function ensureTopSpacer(): HTMLElement {
    let spacer = messagesEl.querySelector('#messages-spacer') as HTMLElement | null
    if (!spacer) {
      spacer = el('<div id="messages-spacer" class="chat-messages-spacer" aria-hidden="true"></div>')
      messagesEl.prepend(spacer)
    }
    return spacer
  }

  async function paintNow(opts: { forceScroll?: boolean; forceRepaint?: boolean } = {}): Promise<void> {
    const loaded = await loadMessages(contactId)
    const polluted = loaded.filter(
      (m) => isTempChatControlPlain(m.text) || isZionControlPlain(m.text),
    )
    if (polluted.length) {
      await Promise.all(polluted.map((m) => deleteMessage(m.id)))
    }
    const msgs = loaded.filter(
      (m) => !isTempChatControlPlain(m.text) && !isZionControlPlain(m.text),
    )
    const wasNearBottom = isNearBottom(160)
    const anchor = userReadingHistory && !opts.forceScroll ? captureScrollAnchor() : null
    const typingNow = userIsTypingInComposer()

    const keyboardLikelyOpen = document.documentElement.classList.contains('keyboard-open')
    const hasNewMessages = msgs.length > lastPaintedMsgs.length
    const lastMsg = msgs.length > 0 ? msgs[msgs.length - 1] : null

    const shouldFollow =
      opts.forceScroll ||
      (!userReadingHistory && wasNearBottom) ||
      (hasNewMessages &&
        lastMsg &&
        (lastMsg.outgoing || keyboardLikelyOpen) &&
        (!typingNow || keyboardLikelyOpen || !lastMsg.outgoing))

    if (msgs.length > lastPaintedMsgs.length && userReadingHistory && !shouldFollow) {
      newMessagesBelow = true
    }

    if (!msgs.length) {
      if (!messagesEl.querySelector('.chat-empty')) {
        messagesEl.replaceChildren(
          el('<div id="messages-spacer" class="chat-messages-spacer" aria-hidden="true"></div>'),
          el(`<p class="status muted chat-empty">${escapeHtml(t('chat_empty'))}</p>`),
          el('<div id="messages-end" aria-hidden="true"></div>'),
        )
      }
      lastPaintedMsgs = []
      updateJumpButton()
      return
    }

    messagesEl.querySelector('.chat-empty')?.remove()

    // CRITICAL: sync usava insertBefore(end) → messaggi in ordine sbagliato (orari “sballati”)
    const orderedFrag = document.createDocumentFragment()
    let prevTs = 0
    for (const m of msgs) {
      if (!prevTs || !sameCalendarDay(prevTs, m.timestamp)) {
        orderedFrag.append(
          el(
            `<div class="day-separator" aria-hidden="true"><span>${escapeHtml(formatMessageDayLabel(m.timestamp))}</span></div>`,
          ),
        )
      }
      prevTs = m.timestamp
      const bubble = await renderBubble(m, msgs, contactId)
      bindBubbleActions(bubble, m)
      orderedFrag.append(bubble)
    }

    const spacer = ensureTopSpacer()
    const end = messagesEndEl()
    messagesEl.replaceChildren(spacer, orderedFrag, end)

    wireMediaBubbleActions(messagesEl, contactId, msgs)
    lastPaintedMsgs = msgs
    void syncEphemeralLifecycle(msgs)

    if (shouldFollow) {
      userReadingHistory = false
      newMessagesBelow = false
      scrollToLatest()
      scrollIosPwaMessagesIfKeyboardOpen(messagesEl)
    } else if (anchor) {
      restoreScrollAnchor(anchor)
    } else if (opts.forceScroll && !userReadingHistory && !typingNow) {
      scrollToLatest()
    }

    restoreComposerDraft()
    updateJumpButton()
  }

  async function syncEphemeralLifecycle(msgs: ChatMessage[]): Promise<void> {
    for (const m of msgs) {
      if (m.outgoing || !isEphemeralMessage(m) || m.readAndDestroyed) continue
      if (!(m.countdownStartedAt && m.countdownStartedAt > 0) && !ephemeralArmed.has(m.id)) {
        ephemeralArmed.add(m.id)
        await armEphemeralCountdown(m.id)
      }
      const remaining = ephemeralRemainingSeconds(m)
      if (remaining <= 0 && m.countdownStartedAt && m.countdownStartedAt > 0) {
        if (!ephemeralExpiring.has(m.id)) {
          ephemeralExpiring.add(m.id)
          void onEphemeralCountdownExpired(m.id).finally(() => ephemeralExpiring.delete(m.id))
        }
      }
    }
    tickEphemeralBadges(msgs)
  }

  function tickEphemeralBadges(msgs: ChatMessage[]): void {
    const byId = new Map(msgs.map((m) => [m.id, m]))
    messagesEl.querySelectorAll<HTMLElement>('.bubble[data-msg-id]').forEach((node) => {
      const id = node.dataset.msgId
      if (!id) return
      const m = byId.get(id)
      if (!m || !isEphemeralMessage(m)) return
      const badge = node.querySelector('.bubble-timer-badge') as HTMLElement | null
      if (!badge) return
      if (m.readAndDestroyed) {
        badge.textContent = '✉'
        return
      }
      if (m.outgoing) {
        badge.textContent = `⏱ ${m.durationSeconds}s`
        return
      }
      const rem = ephemeralRemainingSeconds(m)
      badge.textContent = rem > 0 ? `⏱ ${rem}s` : '⏱ …'
      badge.classList.toggle('bubble-timer-urgent', rem > 0 && rem <= 5)
    })
  }

  function schedulePaint(opts: { forceScroll?: boolean; forceRepaint?: boolean } = {}): void {
    pendingPaintOpts = {
      forceScroll: pendingPaintOpts.forceScroll || opts.forceScroll,
      forceRepaint: pendingPaintOpts.forceRepaint || opts.forceRepaint,
    }
    if (paintRaf != null) return
    paintRaf = requestAnimationFrame(() => {
      paintRaf = null
      const runOpts = pendingPaintOpts
      pendingPaintOpts = {}
      void paintNow(runOpts)
    })
  }

  async function paint(opts: { forceScroll?: boolean; forceRepaint?: boolean } = {}): Promise<void> {
    const msgs = await loadMessages(contactId)
    if (!opts.forceRepaint && !messagesChanged(lastPaintedMsgs, msgs)) {
      if (opts.forceScroll && !userReadingHistory) scrollToLatest()
      return
    }
    if (opts.forceScroll || opts.forceRepaint) {
      await paintNow(opts)
      return
    }
    schedulePaint(opts)
  }

  function bindBubbleActions(node: HTMLElement, msg: ChatMessage): void {
    const open = (): void => openMsgActions(msg)
    node.addEventListener('contextmenu', (e) => {
      e.preventDefault()
      open()
    })
    let pressTimer: number | null = null
    node.addEventListener('touchstart', () => {
      pressTimer = window.setTimeout(open, 480)
    }, { passive: true })
    node.addEventListener('touchend', () => {
      if (pressTimer != null) window.clearTimeout(pressTimer)
    })
    node.addEventListener('touchmove', () => {
      if (pressTimer != null) window.clearTimeout(pressTimer)
    })
    if (!('ontouchstart' in window)) {
      node.addEventListener('click', (e) => {
        if (e.detail === 1 && e.shiftKey) open()
      })
    }
  }

  await paintNow({ forceScroll: true })
  void syncInboundMessages().then(() => paint({ forceScroll: true }))

  const attachPanel = root.querySelector('#attach-panel') as HTMLElement
  const attachToggleBtn = root.querySelector('#btn-attach-toggle') as HTMLButtonElement
  const timerPicker = root.querySelector('#timer-picker') as HTMLElement
  const btnTimer = root.querySelector('#btn-timer') as HTMLButtonElement
  const btnPhoto = root.querySelector('#btn-photo') as HTMLButtonElement
  const btnVoice = root.querySelector('#btn-voice') as HTMLButtonElement
  const btnDocument = root.querySelector('#btn-document') as HTMLButtonElement

  let attachMenuOpen = false
  let timerPickerOpen = false
  let voiceBlocking = false

  function refreshTimerUi(): void {
    const ttl = getMessageTimerTtl(contactId)
    btnTimer.classList.toggle('is-active', ttl != null)
    timerPicker.querySelectorAll<HTMLButtonElement>('.timer-opt').forEach((btn) => {
      const raw = btn.dataset.ttl ?? ''
      const optTtl = raw === '' ? null : Number.parseInt(raw, 10)
      btn.classList.toggle('is-active', optTtl === ttl)
    })
  }

  function setAttachMenuOpen(open: boolean): void {
    if (voiceBlocking && !open) return
    attachMenuOpen = open
    attachPanel.hidden = !open
    attachToggleBtn.textContent = open ? '▲' : '+'
    attachToggleBtn.classList.toggle('is-open', open)
    if (!open) {
      timerPickerOpen = false
      timerPicker.hidden = true
    }
  }

  function setVoiceBlocking(blocked: boolean): void {
    voiceBlocking = blocked
    if (blocked) setAttachMenuOpen(true)
  }

  refreshTimerUi()

  attachToggleBtn.addEventListener('click', () => {
    if (voiceBlocking) return
    setAttachMenuOpen(!attachMenuOpen)
  })

  btnTimer.addEventListener('click', () => {
    timerPickerOpen = !timerPickerOpen
    timerPicker.hidden = !timerPickerOpen
    refreshTimerUi()
  })

  timerPicker.querySelectorAll<HTMLButtonElement>('.timer-opt').forEach((btn) => {
    btn.addEventListener('click', () => {
      const raw = btn.dataset.ttl ?? ''
      const ttl = raw === '' ? null : Number.parseInt(raw, 10)
      setMessageTimerTtl(contactId, Number.isFinite(ttl as number) ? ttl : null)
      timerPickerOpen = false
      timerPicker.hidden = true
      refreshTimerUi()
    })
  })

  btnPhoto.addEventListener('click', (e) => {
    e.preventDefault()
    e.stopPropagation()
    // Su iOS: non chiudere subito il menu nello stesso tick del click() del file input
    // (rompe il user-gesture e il picker non apre / non invia).
    mediaInput.value = ''
    try {
      mediaInput.click()
    } catch {
      /* ignore */
    }
    window.setTimeout(() => setAttachMenuOpen(false), 400)
  })

  btnDocument.addEventListener('click', (e) => {
    e.preventDefault()
    e.stopPropagation()
    documentInput.value = ''
    try {
      documentInput.click()
    } catch {
      /* ignore */
    }
    window.setTimeout(() => setAttachMenuOpen(false), 400)
  })

  function mapVoiceError(code: string): string {
    if (code === CODE_TOO_SHORT) return t('chat_voice_err_short')
    if (code === CODE_TOO_LONG) return t('chat_voice_err_long')
    if (code === 'media_too_large') return t('chat_voice_err_large')
    return t('chat_voice_err_start')
  }

  voiceMicCleanup = wireVoiceMic({
    micBtn: btnVoice,
    recBanner: root.querySelector('#voice-rec-banner') as HTMLElement,
    recTimeEl: root.querySelector('#voice-rec-time') as HTMLElement,
    recCancelBtn: root.querySelector('#voice-rec-cancel') as HTMLElement,
    recStopBtn: root.querySelector('#voice-rec-stop') as HTMLButtonElement,
    previewBanner: root.querySelector('#voice-preview-banner') as HTMLElement,
    previewAudioEl: root.querySelector('#voice-preview-audio') as HTMLAudioElement,
    previewTimeEl: root.querySelector('#voice-preview-time') as HTMLElement,
    previewPlayBtn: root.querySelector('#voice-preview-play') as HTMLButtonElement,
    previewCancelBtn: root.querySelector('#voice-preview-cancel') as HTMLElement,
    previewSendBtn: root.querySelector('#voice-preview-send') as HTMLElement,
    onError: (code) => alert(mapVoiceError(code)),
    onSend: (rec) => {
      setVoiceBlocking(false)
      setAttachMenuOpen(false)
      void (async () => {
        try {
          await sendVoiceMessage(contactId, rec.bytes, rec.mime, rec.durationMs)
          await paint({ forceScroll: true })
        } catch (err) {
          console.warn('[ZION] send voice', err)
          await paint({ forceScroll: true })
        }
      })()
    },
  })

  const voiceRecBanner = root.querySelector('#voice-rec-banner') as HTMLElement
  const voicePreviewBanner = root.querySelector('#voice-preview-banner') as HTMLElement
  const voiceObserver = new MutationObserver(() => {
    const blocked = !voiceRecBanner.hidden || !voicePreviewBanner.hidden
    setVoiceBlocking(blocked)
  })
  voiceObserver.observe(voiceRecBanner, { attributes: true, attributeFilter: ['hidden'] })
  voiceObserver.observe(voicePreviewBanner, { attributes: true, attributeFilter: ['hidden'] })

  root.querySelector('#btn-attach')?.remove()

  mediaInput.addEventListener('change', () => {
    const files = mediaInput.files ? Array.from(mediaInput.files) : []
    mediaInput.value = ''
    if (!files.length) return
    void sendPickedMediaFiles(files)
  })

  documentInput.addEventListener('change', () => {
    const file = documentInput.files?.[0]
    documentInput.value = ''
    if (!file) return
    // GIF selezionata da "Documento": invia come media animato, non come doc.
    const isGif =
      file.type === 'image/gif' ||
      /\.gif$/i.test(file.name) ||
      file.type.toLowerCase().includes('gif')
    if (isGif) {
      void sendPickedMediaFiles([file])
      return
    }
    if (!isDocumentFile(file)) {
      alert(t('chat_doc_unsupported'))
      return
    }
    openDocPreview(file)
  })

  docPreviewModal.querySelector('#doc-preview-cancel')!.addEventListener('click', closeDocPreview)
  docPreviewModal.addEventListener('click', (e) => {
    if (e.target === docPreviewModal) closeDocPreview()
  })
  docPreviewModal.querySelector('#doc-preview-send')!.addEventListener('click', () => {
    const file = pendingDocumentFile
    if (!file || file.size > MAX_DOCUMENT_BYTES) return
    const sendBtn = docPreviewModal.querySelector('#doc-preview-send') as HTMLButtonElement
    const statusEl = docPreviewModal.querySelector('#doc-preview-status') as HTMLElement
    sendBtn.disabled = true
    statusEl.hidden = false
    statusEl.textContent = ''
    void (async () => {
      try {
        await sendMediaMessage(contactId, file, getComposerText(msgInput).trim(), (status) => {
          statusEl.textContent = status
          statusEl.hidden = false
          void paint({ forceRepaint: true })
        })
        clearComposer(msgInput)
        closeDocPreview()
        await paint({ forceScroll: true })
      } catch (err) {
        const errEl = docPreviewModal.querySelector('#doc-preview-error') as HTMLElement
        errEl.hidden = false
        errEl.textContent = String(err)
        statusEl.hidden = true
        sendBtn.disabled = file.size > MAX_DOCUMENT_BYTES
      }
    })()
  })

  composer.addEventListener('submit', async (e) => {
    e.preventDefault()
    await doSendText()
  })

  chatPollTimer = window.setInterval(() => {
    void syncInboundMessages().then(() => paint())
  }, 2000)

  ephemeralTickTimer = window.setInterval(() => {
    if (!lastPaintedMsgs.length) return
    tickEphemeralBadges(lastPaintedMsgs)
    for (const m of lastPaintedMsgs) {
      if (m.outgoing || !isEphemeralMessage(m) || m.readAndDestroyed) continue
      if (!(m.countdownStartedAt && m.countdownStartedAt > 0)) continue
      if (ephemeralRemainingSeconds(m) <= 0 && !ephemeralExpiring.has(m.id)) {
        ephemeralExpiring.add(m.id)
        void onEphemeralCountdownExpired(m.id).finally(() => {
          ephemeralExpiring.delete(m.id)
          void paint({ forceRepaint: true })
        })
      }
    }
  }, 1000)

  chatMessageUnsub = onMessagesChanged((changedContactId) => {
    if (changedContactId !== contactId && changedContactId !== '*') return
    void paint({ forceScroll: !userReadingHistory })
  })

  chatVisibilityHandler = () => {
    if (document.visibilityState === 'visible') {
      void syncInboundMessages().then(() => paint({ forceScroll: true }))
    }
  }
  document.addEventListener('visibilitychange', chatVisibilityHandler)

  chatSwipeCleanup?.()
  chatSwipeCleanup = wireSwipeBack(root, goBackFromChat, {
    ignoreSelector: '.voice-mic-btn, #btn-voice, .voice-recording-banner',
  }).dispose
}

function goBackFromChat(): void {
  setCurrentChatContactId('')
  navigate('home')
}

async function renderBubble(m: ChatMessage, all: ChatMessage[], contactId: string): Promise<HTMLElement> {
  const theme = getCurrentTheme()
  let textColor = ''
  if (theme === 'matrix') {
    textColor = m.outgoing ? '#00ff41' : '#f5f5f5'
  }
  const replyBlock = m.replyToId
    ? buildReplyQuote(m.replyToId, m.replyPreview, all, textColor)
    : ''
  const pending = m.pending ? ' <span class="bubble-pending">…</span>' : ''
  const colorAttr = textColor ? ` style="color:${textColor}"` : ''

  if (m.readAndDestroyed) {
    return el(`
      <div class="bubble ${m.outgoing ? 'out' : 'in'} bubble-destroyed" data-msg-id="${escapeHtml(m.id)}" title="${escapeHtml(t('chat_destroyed_title'))}">
        <p${colorAttr}>${escapeHtml(t('chat_destroyed'))}</p>
        <span class="bubble-timer-badge">✉</span>
        <time>${new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</time>
      </div>
    `)
  }

  const rem =
    !m.outgoing && isEphemeralMessage(m)
      ? ephemeralRemainingSeconds(m)
      : m.durationSeconds && m.durationSeconds > 0
        ? m.durationSeconds
        : 0
  const timerBadge =
    rem > 0 || isEphemeralMessage(m)
      ? `<span class="bubble-timer-badge${rem > 0 && rem <= 5 && !m.outgoing ? ' bubble-timer-urgent' : ''}">⏱ ${rem > 0 ? rem : m.durationSeconds}s</span>`
      : ''
  const hasMedia = isMediaKind(m.mediaKind) || m.text.includes('[Contenuto multimediale')
  const mediaHtml = hasMedia ? await buildMediaBubbleHtml(m, contactId) : ''
  const caption = hasMedia ? mediaCaptionHtml(m.text, escapeHtml) : ''
  const textBlock =
    !hasMedia || caption
      ? `<p${textColor ? ` style="color:${textColor}"` : ''}>${hasMedia ? caption : renderMessageHtml(m.text, escapeHtml)}${pending}</p>`
      : `<p${textColor ? ` style="color:${textColor}"` : ''}>${pending}</p>`

  const bubble = el(`
    <div class="bubble ${m.outgoing ? 'out' : 'in'}${hasMedia ? ' has-media' : ''}" data-msg-id="${escapeHtml(m.id)}" title="${escapeHtml(t('chat_hold_options'))}">
      ${replyBlock}
      ${mediaHtml}
      ${textBlock}
      ${timerBadge}
      <time>${new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</time>
    </div>
  `)

  // Applica DOPO il DOM: controlla testo reale (ignora pending "…")
  if (!hasMedia) {
    const p = bubble.querySelector('p')
    const plainFromDom = (() => {
      if (!p) return ''
      const clone = p.cloneNode(true) as HTMLElement
      clone.querySelectorAll('.bubble-pending').forEach((n) => n.remove())
      return (clone.textContent || '').trim()
    })()
    const emojiSrc = isOnlyEmoji(m.text) ? m.text.trim() : isOnlyEmoji(plainFromDom) ? plainFromDom : ''
    if (emojiSrc && p) {
      bubble.classList.add('emoji-only-message')
      bubble.style.cssText =
        'background:transparent!important;background-image:none!important;border:none!important;box-shadow:none!important;padding:0!important;min-width:0!important;'
      // Span dedicato in px: su iOS/Safari rem+font custom spesso non ingrandiscono le emoji
      const pendingHtml = p.querySelector('.bubble-pending')?.outerHTML ?? ''
      p.replaceChildren()
      const glyph = document.createElement('span')
      glyph.className = 'emoji-only-glyph'
      glyph.textContent = emojiSrc.replace(/[\u200B\u200C\u2060\uFEFF\u00AD]/g, '').trim()
      glyph.setAttribute(
        'style',
        'font-size:56px!important;line-height:1.1!important;display:inline-block!important;font-family:"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif!important;',
      )
      p.append(glyph)
      if (pendingHtml) p.insertAdjacentHTML('beforeend', pendingHtml)
      p.style.cssText =
        'font-size:56px!important;line-height:1.1!important;margin:0!important;font-family:"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif!important;'
    }
  }

  if (!hasMedia) {
    bubble.querySelectorAll('a.msg-link').forEach((a) => {
      a.addEventListener('click', (e) => {
        e.preventDefault()
        const href = (a as HTMLAnchorElement).href
        openExternalUrl(href)
      })
    })
  }
  return bubble
}

function buildReplyQuote(
  replyToId: string,
  preview: string | undefined,
  all: ChatMessage[],
  accentColor: string,
): string {
  const original = all.find((x) => x.id === replyToId)
  const text = preview?.trim() || original?.text?.slice(0, 80) || 'Messaggio'
  const colorStyle = accentColor ? ` style="color:${accentColor};opacity:0.85"` : ''
  return `<blockquote class="reply-quote"${colorStyle}>${escapeHtml(text)}</blockquote>`
}
